import type { WireColor } from '@/api/types/color'

export type ColorFamily =
  | 'All families'
  | 'New colors'
  | 'Gray screens'
  | 'Neutral'
  | 'Brown & tan'
  | 'Red'
  | 'Orange'
  | 'Yellow'
  | 'Green'
  | 'Teal'
  | 'Blue'
  | 'Purple'
  | 'Pink'

export const COLOR_FAMILIES: ColorFamily[] = [
  'All families',
  'New colors',
  'Gray screens',
  'Neutral',
  'Brown & tan',
  'Red',
  'Orange',
  'Yellow',
  'Green',
  'Teal',
  'Blue',
  'Purple',
  'Pink',
]

/** sRGB (0–255) → OKLCH */
function toOklch(R: number, G: number, B: number) {
  const [r, g, b] = [R, G, B]
    .map((v) => v / 255)
    .map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  return { L, C: Math.hypot(a, bb), H: ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360 }
}

const HUE_BANDS: [ColorFamily, number, number][] = [
  ['Red', 11.95, 38],
  ['Orange', 38, 70],
  ['Yellow', 70, 112],
  ['Green', 112, 171.5],
  ['Teal', 171.5, 214.5],
  ['Blue', 214.5, 275],
  ['Purple', 275, 335],
]

/** Classify a color into its display family using its RGB levels and colorType. */
export function familyOf(color: WireColor): ColorFamily {
  if (color.colorType === 'SYSTEM_GRAYS') return 'Gray screens'

  const r = color.redLevel
  const g = color.greenLevel
  const b = color.blueLevel

  if (r == null || g == null || b == null) return 'Neutral'

  const { L, C, H } = toOklch(r, g, b)

  if (C < 0.02) return 'Neutral'

  if (H >= 34 && H <= 104 && (C < 0.058 || L < 0.4 || (L < 0.6 && C < 0.13))) return 'Brown & tan'

  for (const [name, from, to] of HUE_BANDS) {
    if (H >= from && H < to) return name
  }

  return 'Pink'
}

/** "New colors" = those introduced in the newest jobYearStart present across the whole palette. */
export function filterNewColors(all: WireColor[]): WireColor[] {
  const years = all.map((c) => c.jobYearStart ?? 0).filter((y) => y > 0)

  if (years.length === 0) return []

  const newest = Math.max(...years)

  return all.filter((c) => c.jobYearStart === newest)
}

/** Filter a color list by the selected family. Returns the full list for 'All families'. */
export function filterByFamily(colors: WireColor[], family: ColorFamily): WireColor[] {
  if (family === 'All families') return colors
  if (family === 'New colors') return filterNewColors(colors)

  return colors.filter((c) => familyOf(c) === family)
}
